import { Action, Dispatch } from 'redux';
import _ from 'lodash';

import { IAppState } from './reducers';
import { searchAssets } from '../api/vault/searchEngine/api';
import {
  IGameSearchResponse,
  ISearchParams,
  IDataHit,
  IAsset,
  AssetKeys,
  IOrgTagIds,
} from '../api/vault/searchEngine/interfaces';
import { Search } from '../constants/search';

import { Product } from './product';
import {
  selectUserOrgsSecurityProfilesIds, KeyRoleUserVaultRoles, selectIsUserExchangeVendor,
  selectIsUserVaultSuperAdmin,
} from '../state/user/userOrgRole';
import { IconMap } from './exchange/gameScheduleForm';
import { getVendorTeams } from './exchange/vendorData';
import { VAULT_VIDEO_TYPE } from '../constants';
import { rawAssetDetailsInitialState } from './rawAssetDetails';
import { parseUTCStringEndOfDay, parseUTCStringStartOfDay } from '../utils/time';
import { ISecurityProfileOption } from './securityProfiles';

export enum ActionType {
  RequestSearch = 'Search.RequestSearch',
  FinishSearch = 'Search.FinishSearch',
  UpdateQueryParams = 'Search.UpdateQueryParams',
  ResetSort = 'Search.ResetSort',
  UpdateLimit = 'Search.UpdateLimit',
  ResetLimit = 'Search.ResetLimit',
  ResetPage = 'Search.ResetPage',
  ClearResults = 'Search.ClearResults',
  UpdateAssetDetails = 'Search.UpdateAssetDetails',
  ToggleOwnedFilter = 'Search.ToggleOwnedFilter',
  ToggleNoSecurityProfileFilter = 'Search.ToggleNoSecurityProfileFilter',
  ToggleDateTypeFilter = 'Search.ToggleDateTypeFilter',
  UpdateTeamIcons = 'Search.UpdateTeamIcons',
  UpdateQueryString = 'Search.UpdateQueryString',
  AdvancedSecurityProfileFiltersChanged = 'Search.AdvancedSecurityProfileFiltersChanged',
  ClearAllAdvancedFilters = 'Search.ClearAllAdvancedFilters',
}

export interface ISearchAction extends Action<ActionType> {
  payload: any;
}

export type SearchResponseType = IGameSearchResponse | null;

export interface IState {
  searchResponse: SearchResponseType,
  isQuerying: boolean;
  isCreatedDateTypeSearch: boolean;
  limit: number;
  isOwnedFilter: boolean;
  noSecurityProfilesFilter: boolean;
  queryParams: ISearchEngineActionParams;
  teamIcons: IconMap;
  queryString: string;
  selectedSecurityProfileOptions: Array<ISecurityProfileOption>;
}

export interface ISearchEngineActionParams {
  queryString: string;
  size: number;
  page: number;
  fromDate: Date;
  toDate: Date;
  dateType: 'game' | 'created';
  sortOrder: 'asc' | 'desc';
  sortField: string;
  filters?: Array<any>;
  exclusions?: Array<any>;
}

export const SEARCH_DATE_TYPE_STORAGE_KEY = 'catapult:searchDateTypeFilter';
export const SEARCH_OWNED_FILTER_STORAGE_KEY = 'catapult:searchOwnedFilter';
export const SEARCH_NO_SECURITY_PROFILE_FILTER_STORAGE_KEY = 'catapult:searchNoSecurityProfileFilter';
export const SEARCH_SELECTED_SECURITY_PROFILES_FILTER_STORAGE_KEY = 'catapult:searchSelectedSecurityProfilesFilter';
const safeParseJSON = (key: string, defaultValue: any = null) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored && stored !== 'undefined') {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Invalid JSON
  }
  return defaultValue;
};

const INITIAL_STATE: IState = {
  queryString: '',
  searchResponse: null,
  isQuerying: false,
  isCreatedDateTypeSearch: !!safeParseJSON(SEARCH_DATE_TYPE_STORAGE_KEY),
  limit: Search.DEFAULT_RESULTS_LIMIT_SIZE,
  isOwnedFilter: !!safeParseJSON(SEARCH_OWNED_FILTER_STORAGE_KEY),
  noSecurityProfilesFilter: !!safeParseJSON(SEARCH_NO_SECURITY_PROFILE_FILTER_STORAGE_KEY),
  selectedSecurityProfileOptions: safeParseJSON(SEARCH_SELECTED_SECURITY_PROFILES_FILTER_STORAGE_KEY, []),
  queryParams: {
    queryString: '',
    size: Search.DEFAULT_RESULTS_LIMIT_SIZE,
    page: Search.DEFAULT_PAGE,
    fromDate: null,
    toDate: null,
    dateType: 'game',
    sortOrder: 'desc',
    sortField: 'createdDate',
  },
  teamIcons: null,
};

export default (state: IState = INITIAL_STATE, action: ISearchAction) => {
  const isEmpty = state.queryParams.queryString === '';
  switch (action.type) {
    case ActionType.RequestSearch:
    case ActionType.FinishSearch:
    case ActionType.UpdateAssetDetails:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.UpdateQueryString:
      return {
        ...state,
        queryString: action.payload,
      };
    case ActionType.UpdateQueryParams:
      return {
        ...state,
        queryParams: {
          ...state.queryParams,
          ...action.payload,
        },
      };
    case ActionType.ResetSort:
      return {
        ...state,
        queryParams: {
          ...state.queryParams,
          sortField: isEmpty ? INITIAL_STATE.queryParams.sortField : '',
          sortOrder: isEmpty ? INITIAL_STATE.queryParams.sortOrder : '',
        },
      };
    case ActionType.UpdateLimit:
      return {
        ...state,
        limit: action.payload,
      };
    case ActionType.ResetLimit:
      return {
        ...state,
        limit: INITIAL_STATE.limit,
        queryParams: {
          ...state.queryParams,
          size: INITIAL_STATE.queryParams.size,
        },
      };
    case ActionType.ResetPage:
      return {
        ...state,
        queryParams: {
          ...state.queryParams,
          page: INITIAL_STATE.queryParams.page,
        },
      };
    case ActionType.ClearResults:
      return {
        ...INITIAL_STATE,
        teamIcons: state.teamIcons,
      };
    case ActionType.ToggleOwnedFilter:
      localStorage.setItem(SEARCH_OWNED_FILTER_STORAGE_KEY, (!state.isOwnedFilter).toString());
      return {
        ...state,
        isOwnedFilter: !state.isOwnedFilter,
      };
    case ActionType.ToggleNoSecurityProfileFilter:
      localStorage.setItem(SEARCH_NO_SECURITY_PROFILE_FILTER_STORAGE_KEY, (!state.noSecurityProfilesFilter).toString());
      return {
        ...state,
        noSecurityProfilesFilter: !state.noSecurityProfilesFilter,
      };
    case ActionType.ToggleDateTypeFilter:
      localStorage.setItem(SEARCH_DATE_TYPE_STORAGE_KEY, (!state.isCreatedDateTypeSearch).toString());
      return {
        ...state,
        isCreatedDateTypeSearch: !state.isCreatedDateTypeSearch,
      };
    case ActionType.UpdateTeamIcons:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.AdvancedSecurityProfileFiltersChanged:
      localStorage.setItem(SEARCH_SELECTED_SECURITY_PROFILES_FILTER_STORAGE_KEY, JSON.stringify(action.payload));
      return {
        ...state,
        selectedSecurityProfileOptions: action.payload,
      };
    case ActionType.ClearAllAdvancedFilters:
      localStorage.removeItem(SEARCH_SELECTED_SECURITY_PROFILES_FILTER_STORAGE_KEY);
      localStorage.removeItem(SEARCH_OWNED_FILTER_STORAGE_KEY);
      localStorage.removeItem(SEARCH_NO_SECURITY_PROFILE_FILTER_STORAGE_KEY);
      return {
        ...state,
        selectedSecurityProfileOptions: [],
        noSecurityProfilesFilter: false,
        isOwnedFilter: false,
      };
    default:
      return state;
  }
};

const addVideoTypeFilter = (filters: Array<any>, videoType: string | Array<string>) => {
  const videoTypes = Array.isArray(videoType) ? videoType : [videoType];
  const videoTypeIndex = filters.findIndex(([name, value]) => name === 'videoType');
  if (filters.length && videoTypeIndex !== -1) {
    const [name, value] = filters[videoTypeIndex];
    filters.splice(videoTypeIndex, 1, [name, _.uniq([...videoTypes, ...value])]);
  } else {
    filters.push(['videoType', videoTypes]);
  }
};

const getAdditionalFilters = (queryString: string): any[] => {
  const filters: any[] = [];
  const regex = /{([^}]+)}/g;
  const regexResult = regex.exec(queryString);
  if (regexResult && regexResult[0]) {
    try {
      const additionalFilters = JSON.parse(regexResult[0]);
      const assetKeyValues = Object.values(AssetKeys);
      Object.keys(additionalFilters).forEach((key) => {
        if (assetKeyValues.includes(key)) filters.push([key, [additionalFilters[key]]]);
      });
    } catch (e) {
      // Bad json we can ignore
    }
  }
  return filters;
};

const removeAdditionalFilters = (queryString: string) => queryString.replace(/{([^}]+)}/g, '');

export const queryGamesAction = (timeout: number = 0, sideEffect: () => void = null, firstSearch: boolean = false) => async (
  dispatch: Dispatch<ISearchAction>,
  getState: () => IAppState,
) => {
  dispatch({
    payload: {
      isQuerying: true,
    },
    type: ActionType.RequestSearch,
  });

  const state = getState();

  const product = state.product?.target;
  const queryParams = state.search?.queryParams;
  const isOwnedFilter = state.search?.isOwnedFilter ?? false;
  const noSecurityProfilesFilter = state.search?.noSecurityProfilesFilter ?? false;
  const isCreatedDateTypeSearch = state.search?.isCreatedDateTypeSearch ?? false;
  const selectedSecurityProfileOptions = state.search?.selectedSecurityProfileOptions ?? [];
  const { filterAssets } = state;
  const loginAs = state.user?.userOrgRole?.loginAs;

  const isVault = product === Product.Vault;
  const isExchange = product === Product.Exchange;
  const isMyVault = product === Product.MyVault;

  // TODO: remove timeout once VA-1136 addressed
  setTimeout(async () => {
    // TODO: error dispatch in times when the API might be down
    try {
      const searchParams: ISearchParams = {
        ...queryParams,
      };

      if (Object.keys(filterAssets.filters) !== Object.keys({})) {
        searchParams.filters = Object.entries(filterAssets.filters);
      }

      // Check if vendor
      const role = loginAs[KeyRoleUserVaultRoles][0].roleName;
      const isVendor = role === 'exchangeVendor' || role === 'exchangeVendorPartner';

      if ((isExchange || isOwnedFilter) && !isVendor) {
        const userOrgsSecurityProfiles = selectUserOrgsSecurityProfilesIds(getState());
        // if exchange product or owned assets toggled
        // - ensure we're querying for assets owned by current user's organization
        if (userOrgsSecurityProfiles.length) {
          const filters = [...searchParams.filters];
          const ownerIndex = filters.findIndex(([name, value]) => name === 'owner');
          if (filters.length && ownerIndex !== -1) {
            const [name, value] = filters[ownerIndex];
            filters.splice(ownerIndex, 1, [name, _.uniq([...userOrgsSecurityProfiles, ...value])]);
          } else {
            filters.push(['owner', userOrgsSecurityProfiles]);
          }
          searchParams.filters = filters;
        }
      }

      if (isVault) {
        // if vault licensing
        // - ensure we're excluding exchange and myvault assets from results
        searchParams.exclusions = [['videoType', [VAULT_VIDEO_TYPE.Exchange, VAULT_VIDEO_TYPE.MyVault, VAULT_VIDEO_TYPE.SelfScout]]];

        // Add additional filters from search query if they exist
        if (searchParams.queryString) {
          const additionalFilters = getAdditionalFilters(searchParams.queryString);
          if (additionalFilters.length > 0) searchParams.filters = searchParams.filters.concat(additionalFilters);
          searchParams.queryString = removeAdditionalFilters(searchParams.queryString);
        }
      }

      // Trim whitespace
      searchParams.queryString = searchParams.queryString.trim();

      if (isExchange || isMyVault) {
        const filters = [...searchParams.filters];
        if (isExchange) {
          // if exchange product:
          // - ensure we're querying for assets on the home screen with a videoType of 'Exchange'
          addVideoTypeFilter(filters, VAULT_VIDEO_TYPE.Exchange);
        } else if (isMyVault) {
          // if myvault product:
          // - ensure we're querying for assets on the home screen with a videoType of 'MyVault'
          // - ensure we're querying for assets on the home screen with a videoType of 'SelfScout'
          addVideoTypeFilter(filters, [VAULT_VIDEO_TYPE.MyVault, VAULT_VIDEO_TYPE.SelfScout]);
        }
        searchParams.filters = filters;
      }

      if (isCreatedDateTypeSearch) {
        searchParams.dateType = 'created';
      } else {
        searchParams.dateType = 'game';
      }

      if (noSecurityProfilesFilter && selectIsUserVaultSuperAdmin(state) && isVault) {
        searchParams.filters.push(['securityProfiles', ['N/A']]);
      }

      if (selectedSecurityProfileOptions && selectedSecurityProfileOptions.length > 0 && selectIsUserVaultSuperAdmin(state) && isVault) {
        const currentSelectedSecurityProfileOptions = selectedSecurityProfileOptions.map((option) => option._id);
        searchParams.filters.push(['securityProfiles', currentSelectedSecurityProfileOptions]);
      }

      // from and to date must be utc strings
      if (searchParams.dateType === 'game') {
        if (searchParams.fromDate) {
          searchParams.fromDate = parseUTCStringStartOfDay(searchParams.fromDate);
        }
        if (searchParams.toDate) {
          searchParams.toDate = parseUTCStringEndOfDay(searchParams.toDate);
        }
      }

      const response = await searchAssets(searchParams, state.user.impersonation.user);

      const { location: { search } } = window;
      const params = new URLSearchParams(search);
      const assetId = params.get('assetId');
      if (!firstSearch && !assetId) rawAssetDetailsInitialState()(dispatch, getState);

      dispatch({
        payload: {
          searchResponse: response.data.data,
          isQuerying: false,
        },
        type: ActionType.FinishSearch,
      });
    } catch (e) {
      dispatch({
        payload: {
          isQuerying: false,
        },
        type: ActionType.FinishSearch,
      });
    }

    if (sideEffect) {
      sideEffect();
    }
  }, timeout);
};

export const updateSearchQueryParams = (params: object) => ({
  type: ActionType.UpdateQueryParams,
  payload: params,
});

export const updateSearchQueryString = (queryString: string) => ({
  type: ActionType.UpdateQueryString,
  payload: queryString,
});

export const resetSort = () => ({
  type: ActionType.ResetSort,
});

export const updateLimit = (value: number) => ({
  type: ActionType.UpdateLimit,
  payload: value,
});

export const resetLimit = () => ({
  type: ActionType.ResetLimit,
});

export const resetPage = () => ({
  type: ActionType.ResetPage,
});

export const clearSearchResults = () => ({
  type: ActionType.ClearResults,
});

export const updateSearchHit = (assetDetails: any) => async (
  dispatch: Dispatch<ISearchAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  const searchResponse = state.search?.searchResponse;
  if (!searchResponse?.hits?.hits) return;
  const { hits } = searchResponse.hits;

  const assetIndex: number = hits.findIndex((h) => h._id === assetDetails._id);
  if (assetIndex === -1) return;
  const asset: IDataHit<IAsset> = hits[assetIndex];

  searchResponse.hits.hits[assetIndex] = {
    ...asset,
    _source: {
      // update search hit fields w/ provided asset details
      ...asset._source,
      ...assetDetails,
    },
  };

  searchResponse.hits.hits = [...searchResponse.hits.hits];
  dispatch({
    type: ActionType.UpdateAssetDetails,
    payload: {
      searchResponse: { ...searchResponse },
    },
  });
};

export const updateSearchHits = (assetDetails: any[]) => async (
  dispatch: Dispatch<ISearchAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  const searchResponse = state.search?.searchResponse;
  if (!searchResponse?.hits?.hits) return;
  const { hits } = searchResponse.hits;

  assetDetails.forEach((assetDetails) => {
    const assetIndex: number = hits.findIndex((h) => h._id === assetDetails._id);
    const asset: IDataHit<IAsset> = hits[assetIndex];
    searchResponse.hits.hits[assetIndex] = {
      ...asset,
      _source: {
        // update search hit fields w/ provided asset details
        ...asset._source,
        ...assetDetails,
      },
    };
  });

  searchResponse.hits.hits = [...searchResponse.hits.hits];
  dispatch({
    type: ActionType.UpdateAssetDetails,
    payload: {
      searchResponse,
    },
  });
};

export const updateSearchHitTags = (assetId: string, orgTagIds: IOrgTagIds[]) => async (
  dispatch: Dispatch<ISearchAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  const searchResponse = state.search?.searchResponse;
  if (!searchResponse?.hits?.hits) return;
  const { hits } = searchResponse.hits;

  const assetIndex: number = hits.findIndex((h) => h._id === assetId);
  if (assetIndex === -1) return;
  const asset: IDataHit<IAsset> = hits[assetIndex];

  const updatedAsset: IDataHit<IAsset> = {
    ...asset,
    // @ts-ignore
    _source: {
      // update search hit fields w/ provided asset details
      ...asset._source,
      orgTagIds: [...orgTagIds],
    },
  };

  searchResponse.hits.hits[assetIndex] = updatedAsset;

  dispatch({
    type: ActionType.UpdateAssetDetails,
    payload: {
      searchResponse,
    },
  });
};

export const updateSearchHitAddTag = (assetId: string, orgTagId: IOrgTagIds) => async (
  dispatch: Dispatch<ISearchAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  const searchResponse = state.search?.searchResponse;
  if (!searchResponse?.hits?.hits) return;
  const { hits } = searchResponse.hits;
  const assetIndex: number = hits.findIndex((h) => h._id === assetId);
  if (assetIndex === -1) return;
  const asset: IDataHit<IAsset> = hits[assetIndex];
  const currentOrgTags: IOrgTagIds[] = asset._source.orgTagIds;
  if (!currentOrgTags.find((e) => e._id === orgTagId._id)) {
    currentOrgTags.push(orgTagId);
  }

  const updatedAsset: IDataHit<IAsset> = {
    ...asset,
    // @ts-ignore
    _source: {
      // update search hit fields w/ provided asset details
      ...asset._source,
      orgTagIds: [...currentOrgTags],
    },
  };

  searchResponse.hits.hits[assetIndex] = updatedAsset;
  searchResponse.hits.hits = [...searchResponse.hits.hits];
  dispatch({
    type: ActionType.UpdateAssetDetails,
    payload: {
      searchResponse: { ...searchResponse },
    },
  });
};

export const updateSearchHitRemoveTag = (assetId: string, orgTagId: IOrgTagIds) => async (
  dispatch: Dispatch<ISearchAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  const searchResponse = state.search?.searchResponse;
  const selectedFilterOptions = state.filterAssets;
  if (!searchResponse?.hits?.hits) return;
  const { hits } = searchResponse.hits;
  const assetIndex: number = hits.findIndex((h) => h._id === assetId);
  if (assetIndex === -1) return;
  const asset: IDataHit<IAsset> = hits[assetIndex];
  let currentOrgTags: IOrgTagIds[] = asset._source.orgTagIds;
  currentOrgTags = currentOrgTags.filter((oldTag) => oldTag._id !== orgTagId._id);
  const filterTags = selectedFilterOptions?.selectedFilterOptions?.orgTagIds?.map((i) => i.value);
  const currentIds = currentOrgTags.map((org) => org._id);

  const updatedAsset: IDataHit<IAsset> = {
    ...asset,
    // @ts-ignore
    _source: {
      // update search hit fields w/ provided asset details
      ...asset._source,
      orgTagIds: [...currentOrgTags],
    },
  };

  searchResponse.hits.hits[assetIndex] = updatedAsset;
  searchResponse.hits.hits = [...searchResponse.hits.hits];
  if (filterTags && !currentIds.some((tagId) => filterTags.includes(tagId))) searchResponse.hits.hits.splice(assetIndex, 1);
  dispatch({
    type: ActionType.UpdateAssetDetails,
    payload: {
      searchResponse: { ...searchResponse },
    },
  });
};

export const selectSearchHitById = (state: IAppState, assetId: string): IDataHit<IAsset> | null => {
  const searchResponse = state.search?.searchResponse;
  if (!searchResponse?.hits?.hits) return null;
  const { hits } = searchResponse.hits;
  const assetIndex: number = hits.findIndex((h) => h._id === assetId);
  if (assetIndex === -1) return null;
  const asset: IDataHit<IAsset> = hits[assetIndex];
  return asset;
};

export const toggleOwnedFilter = () => ({
  type: ActionType.ToggleOwnedFilter,
});

export const toggleNoSecurityProfileFilter = () => ({
  type: ActionType.ToggleNoSecurityProfileFilter,
});

export const toggleDateTypeFilter = () => ({
  type: ActionType.ToggleDateTypeFilter,
});

export const onAdvancedSecurityProfileFiltersChanged = (selectedSecurityProfiles: ISecurityProfileOption[]) => ({
  type: ActionType.AdvancedSecurityProfileFiltersChanged,
  payload: selectedSecurityProfiles,
});

export const clearAllAdvancedFilters = () => ({
  type: ActionType.ClearAllAdvancedFilters,
});

export const getTeamIconsAction = () => async (
  dispatch: Dispatch<ISearchAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  // Create map for team icons to generate exchange thumbnails
  const teamsResponse = await getVendorTeams(selectIsUserExchangeVendor(state), true);
  const teamIconsMap: IconMap = (teamsResponse.data?.data || []).reduce((container: IconMap, { _id, TeamImage }) => {
    if (!container[_id]) {
      return {
        ...container,
        [_id]: TeamImage,
      };
    }
    return container;
  }, {});
  dispatch({
    type: ActionType.UpdateTeamIcons,
    payload: {
      teamIcons: teamIconsMap,
    },
  });
};

export const selectSearchHitByAssetId = (state: IAppState): IDataHit<IAsset> => {
  if (state.search.searchResponse.hits.hits.length > 0) {
    const searchHits = [...state.search.searchResponse.hits.hits];
    const assetIndex: number = searchHits.findIndex((h) => h._id === state.tags.currentAssetId);
    const asset: IDataHit<IAsset> = searchHits[assetIndex];
    return asset;
  }
  return null;
};

export const selectSearchResponse = (state: IAppState): SearchResponseType => state.search.searchResponse;

export const selectSelectedSecurityProfileOptions = (state: IAppState) => state.search.selectedSecurityProfileOptions;
